import {
  BadgeGroup,
  AskLubavResponse,
  COLOR_TOKENS,
  SPACING,
  ResourcePagesType,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ActionButtons } from '@backrs_ui/grow-ui/src/components/MakeItPersonal/ActionButtons';
import { MakeItPersonalHeader } from '@backrs_ui/grow-ui/src/components/MakeItPersonal/MakeItPersonalHeader';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { MakeItPersonalBadge } from '@backrs_ui/grow-ui/src/redux/makeItPersonal/makeItPersonal';
import { selectProfilePhoto } from '@backrs_ui/grow-ui/src/redux/user/user';

import { Box, Theme, SxProps } from '@mui/material';
import Markdown from 'react-markdown';

export type MakeItPersonalProps = Readonly<{
  pageName: ResourcePagesType;
  badges: MakeItPersonalBadge[];
  activeBadge?: MakeItPersonalBadge;
  onBadgeClick: (badge: MakeItPersonalBadge) => void;
  collapsedByDefault?: boolean;
}>;

export const MakeItPersonal = ({
  pageName,
  badges,
  activeBadge,
  onBadgeClick,
  collapsedByDefault = false,
}: MakeItPersonalProps) => {
  const profilePicture = useAppSelector(selectProfilePhoto);

  const errorMessage = (
    <Box sx={errorMessageSx}>
      <Markdown>{appStrings.modules.chatBox.overloadErr}</Markdown>
    </Box>
  );

  // Helper to determine if response is a valid markdown string
  const isValidResponse = (response: unknown): response is string => {
    return typeof response === 'string' && response.trim() !== '';
  };

  // Helper to determine if response is an error
  const isErrorResponse = (response: unknown): boolean => {
    return (
      response !== '' && response !== undefined && response !== null && !isValidResponse(response)
    );
  };

  const isLoading = (badges: MakeItPersonalBadge[]): boolean => {
    return badges.some((badge) => badge.isLoading);
  };

  return (
    <Box sx={askLubavSidebar}>
      <MakeItPersonalHeader profilePicture={profilePicture || undefined} pageName={pageName} />
      <Box sx={badgesContainerSx}>
        {badges.map((badge) => (
          <Box key={badge.id}>
            <BadgeGroup
              key={badge.id}
              badgeName={badge.badgeName}
              title={badge.title}
              isOpen={activeBadge?.id === badge.id && badge.response.trim() !== ''}
              setIsOpen={() => (!isLoading(badges) ? onBadgeClick(badge) : undefined)}
              loading={badge.isLoading}
            >
              <Box>
                {isValidResponse(badge.response) && (
                  <Box sx={askLubavTextSx}>
                    <AskLubavResponse
                      response={badge.response}
                      tier={undefined}
                      apiKeyAction={{ key: badge.endpoint }}
                      containerSx={{
                        backgroundColor: COLOR_TOKENS['Colors/Base/White'],
                        padding: `${SPACING['spacing-none']} !important`,
                      }}
                      contentSize="SMALL"
                      collapsedByDefault={collapsedByDefault}
                    />
                  </Box>
                )}

                {!badge.isLoading && isErrorResponse(badge.response) && errorMessage}
              </Box>
            </BadgeGroup>
            {activeBadge?.id === badge.id && !badge.isLoading && (
              <ActionButtons generatedContent={badge.generatedContent} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const badgesContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  px: backrs.spacing['spacing-3xl'],
  gap: backrs.spacing['spacing-md'],
});

const askLubavTextSx: SxProps<Theme> = ({ backrs }) => ({
  pb: backrs.spacing['spacing-xl'],
  px: backrs.spacing['spacing-lg'],
});

const errorMessageSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Error/800'],
  pb: backrs.spacing['spacing-md'],
  px: backrs.spacing['spacing-lg'],
});

const askLubavSidebar: SxProps<Theme> = ({ backrs }) => ({
  borderLeft: { md: `1px solid ${backrs.colors['Colors/Base/Black']}` },
  borderTop: { xs: `1px solid ${backrs.colors['Colors/Base/Black']}`, md: 'none' },
  '@media (max-width: 414px)': {
    borderLeft: 'none',
  },
  width: {
    xs: '100%',
    sm: '100%',
    lg: '468px',
  },
  '@media (min-width: 900px)': {
    maxWidth: '300px',
  },
  '@media (min-width: 1200px)': {
    maxWidth: '368px',
  },
  '@media (min-width: 1536px)': {
    maxWidth: '468px',
  },
  objectFit: 'cover',
  height: { md: '100svh' },
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  paddingBottom: { xs: backrs.spacing['spacing-md'], md: 'none' },
  overflowY: { md: 'auto' },
});
